/**
 * AriaPulse — el "parte de actividad de ARIA": lo que el agente hizo por su
 * cuenta (hechas), lo que propone y espera tu OK, y lo que frenó para
 * protegerte. Es un EJE DISTINTO de la Bandeja (el to-do priorizado): el pulso
 * responde "¿qué ha hecho ARIA por mí?", la Bandeja "¿qué me toca resolver?".
 * Por eso tiene identidad propia ("Actividad de ARIA") y su 0/0/0 significa
 * "ARIA no ha actuado sola", nunca "la bandeja está vacía".
 *
 * Dos variantes desde el mismo hook (`useAriaPulse`):
 *   - variant="chip" → píldora compacta para la TopBar; resalta solo si hay
 *     atención pendiente, en reposo queda en calma. Abre el parte en un popover.
 *   - variant="full" → tarjeta ancha para el Home, con estados tangibles
 *     (cada contador lleva su último ejemplo) y estado vacío confiado.
 *
 * Drill-down por contador: "esperan tu OK" y "frenadas" abren su lista en la
 * Bandeja de ARIA; "hechas" es informativo (su detalle vive en el historial).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, CheckCircle2, Clock, ShieldAlert, ArrowRight, ChevronDown, PenLine } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useAriaPulse } from '@/hooks/useAriaPulse';

const BANDEJA = '/aria/bandeja';
// "Lo que ARIA ha contabilizado por ti" — el detalle clicable de "Hechas".
const ACTIVIDAD = '/aria/actividad';
// Los partes diarios firmables viven en el Resumen contable.
const DAILY_REPORTS = '/accounting';

// `label`/`prefix` son los defaults en español; se traducen en render vía
// `t(labelKey, { defaultValue: label })` (constante a nivel de módulo → sin hooks aquí).
const SEGMENTS = [
  {
    // Tokens y no escalas crudas: `text-amber-600` sobre blanco da 3.18 contra
    // el 4.5 de AA, y este chip vive en la barra superior — salia en LAS ONCE
    // rutas medidas en cuanto el contador pasaba de cero, incluidas dos que
    // estaban a cero violaciones. Con `warning` (recalibrado en erp#2521) son
    // 5.21, y el modo oscuro deja de necesitar su propia variante.
    key: 'done',
    label: 'Hechas',
    labelKey: 'ai.pulse_done',
    Icon: CheckCircle2,
    to: ACTIVIDAD, // el detalle: la torre de control con el botón Deshacer
    dot: 'bg-success',
    text: 'text-success',
    soft: 'bg-success/12',
    prefix: 'Última:',
    prefixKey: 'ai.pulse_last_prefix',
  },
  {
    key: 'waiting',
    label: 'Esperan tu OK',
    labelKey: 'ai.pulse_waiting',
    Icon: Clock,
    to: BANDEJA,
    dot: 'bg-warning',
    text: 'text-warning',
    soft: 'bg-warning/12',
    prefix: null,
  },
  {
    key: 'blocked',
    label: 'Frenadas',
    labelKey: 'ai.pulse_blocked',
    Icon: ShieldAlert,
    to: BANDEJA,
    dot: 'bg-destructive',
    text: 'text-destructive',
    soft: 'bg-destructive/12',
    prefix: null,
  },
];

function AriaBadge({ size = 'sm' }) {
  const cls = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';
  const ico = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  return (
    <span className={`flex ${cls} shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground`}>
      <Sparkles className={ico} />
    </span>
  );
}

/** Cabecera del parte, compartida por el widget y el popover. */
function PulseHeader({ attention }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <AriaBadge size="md" />
      <div className="leading-tight">
        <div className="text-sm font-semibold">{t('ai.pulse_title', { defaultValue: 'Actividad de ARIA' })}</div>
        <span className="font-mono text-[11px] text-muted-foreground">{t('ai.pulse_last_24h', { defaultValue: 'últimas 24 h' })}</span>
      </div>
      {attention > 0 && (
        <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-primary">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:hidden" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
          </span>
          {t('ai.pulse_news', { defaultValue: 'Novedades' })}
        </span>
      )}
    </div>
  );
}

/** Un bloque de contador: icono + número grande + último ejemplo. */
function StatBlock({ seg, value, example, onNavigate, dense = false }) {
  const { t } = useTranslation();
  const { key, label, labelKey, Icon, to, text, soft, prefix, prefixKey } = seg;
  const navigable = !!to && value > 0;
  const Wrapper = navigable ? 'button' : 'div';
  const isZero = !value;

  return (
    <Wrapper
      type={navigable ? 'button' : undefined}
      onClick={navigable ? () => onNavigate(to) : undefined}
      data-testid={`aria-pulse-stat-${key}`}
      className={[
        'group block w-full text-left',
        dense ? 'px-3 py-2' : 'px-4 py-3.5',
        navigable ? 'cursor-pointer transition-colors hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary' : 'cursor-default',
      ].join(' ')}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className={`flex h-6 w-6 items-center justify-center rounded-lg ${soft}`}>
          <Icon className={`h-3.5 w-3.5 ${text}`} />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">{t(labelKey, { defaultValue: label })}</span>
        {navigable && (
          <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:opacity-100" />
        )}
      </div>
      <div className={`font-mono tabular-nums font-bold leading-none tracking-tight ${dense ? 'text-2xl' : 'text-[32px]'} ${isZero ? 'text-muted-foreground' : text}`}>
        {value || 0}
      </div>
      {example && (
        <p className="mt-1.5 truncate text-xs text-muted-foreground">
          {prefix && <span>{t(prefixKey, { defaultValue: prefix })} </span>}
          <span className="font-medium text-foreground/70">{example}</span>
        </p>
      )}
    </Wrapper>
  );
}

/** Estado de reposo: nada que ARIA haya hecho por su cuenta. */
function PulseEmpty({ dense = false }) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-3 ${dense ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-success/12 text-success">
        <CheckCircle2 className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold">{t('ai.pulse_empty_title', { defaultValue: 'ARIA está al día' })}</h4>
        <p className="text-xs text-muted-foreground">{t('ai.pulse_empty_desc', { defaultValue: 'Sin acciones autónomas en 24 h. Te avisaré aquí en cuanto haga algo.' })}</p>
      </div>
    </div>
  );
}

/** Cuerpo del parte (grid de contadores o estado vacío). Reutilizado en popover. */
function PulseBody({ pulse, total, onNavigate, dense = false }) {
  if (total === 0) return <PulseEmpty dense={dense} />;
  const last = pulse.last && typeof pulse.last === 'object' ? pulse.last : {};
  return (
    <div className={`grid ${dense ? 'grid-cols-1 divide-y' : 'grid-cols-1 divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0'} divide-border`}>
      {SEGMENTS.map((seg) => (
        <StatBlock
          key={seg.key}
          seg={seg}
          value={pulse[seg.key] || 0}
          example={last[seg.key] || null}
          onNavigate={onNavigate}
          dense={dense}
        />
      ))}
    </div>
  );
}

/** Widget ancho para el Home — COLAPSABLE: por defecto una sola fila-botón con
 * el resumen de estado (al día / contadores) + chevron; al pulsar despliega el
 * parte completo. Colapsado libera espacio vertical para que el panel de ARIA
 * entre sin scroll. */
function FullVariant({ pulse, total, onNavigate }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const attention = (pulse.waiting || 0) + (pulse.blocked || 0);
  return (
    <div
      data-testid="aria-pulse-full"
      className={[
        'overflow-hidden rounded-2xl border bg-primary/[0.03] shadow-sm dark:bg-primary/[0.06]',
        attention > 0 ? 'border-primary/40 ring-1 ring-primary/15' : 'border-primary/20',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="aria-pulse-full-toggle"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-primary/[0.05]"
      >
        <AriaBadge size="md" />
        <div className="leading-tight">
          <div className="text-sm font-semibold">{t('ai.pulse_title', { defaultValue: 'Actividad de ARIA' })}</div>
          <span className="font-mono text-[11px] text-muted-foreground">{t('ai.pulse_last_24h', { defaultValue: 'últimas 24 h' })}</span>
        </div>
        {/* Resumen de estado inline (mismo lenguaje que el chip de la TopBar). */}
        <span className="ml-auto flex items-center gap-2.5">
          {total === 0 ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
              {t('ai.pulse_up_to_date', { defaultValue: 'al día' })}
            </span>
          ) : (
            SEGMENTS.map(({ key, dot, text }) => (
              <span key={key} className="flex items-center gap-1">
                <span className={`h-1.5 w-1.5 rounded-full ${dot} ${(pulse[key] || 0) === 0 ? 'opacity-30' : ''}`} />
                <span className={`font-mono text-xs font-semibold tabular-nums ${(pulse[key] || 0) === 0 ? 'text-muted-foreground' : text}`}>
                  {pulse[key] || 0}
                </span>
              </span>
            ))
          )}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="border-t border-primary/15">
          <PulseBody pulse={pulse} total={total} onNavigate={onNavigate} />
        </div>
      )}
    </div>
  );
}

/** Chip compacto para la TopBar + popover con el parte. */
function ChipVariant({ pulse, total, onNavigate }) {
  const { t } = useTranslation();
  const attention = (pulse.waiting || 0) + (pulse.blocked || 0);
  const toSign = pulse.to_sign || 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="aria-pulse-chip"
          aria-label={
            t('ai.pulse_chip_label', {
              defaultValue: 'Actividad de ARIA: {{done}} hechas, {{waiting}} esperan tu OK, {{blocked}} frenadas',
              done: pulse.done || 0,
              waiting: pulse.waiting || 0,
              blocked: pulse.blocked || 0,
            }) + (toSign > 0 ? t('ai.pulse_chip_tosign', { defaultValue: ', {{n}} partes por firmar', n: toSign }) : '')
          }
          className={[
            'hidden md:inline-flex h-9 items-center gap-2 rounded-full border bg-card px-2.5 text-xs transition-colors',
            attention > 0 ? 'border-primary/60 ring-2 ring-primary/15' : 'border-border hover:bg-muted/60',
          ].join(' ')}
        >
          <AriaBadge size="sm" />
          {total === 0 ? (
            <span className="flex items-center gap-1.5 pr-1 font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
              {t('ai.pulse_up_to_date', { defaultValue: 'al día' })}
            </span>
          ) : (
            SEGMENTS.map(({ key, dot, text }) => (
              <span key={key} className="flex items-center gap-1">
                <span className={`h-1.5 w-1.5 rounded-full ${dot} ${(pulse[key] || 0) === 0 ? 'opacity-30' : ''}`} />
                <span className={`font-mono font-semibold tabular-nums ${(pulse[key] || 0) === 0 ? 'text-muted-foreground' : text}`}>
                  {pulse[key] || 0}
                </span>
              </span>
            ))
          )}
          {/* Partes por firmar (N-FE3): eje aparte, punto ámbar; NO infla el total. */}
          {toSign > 0 && (
            <span data-testid="aria-pulse-tosign" className="flex items-center gap-1 border-l border-border pl-1.5" title={t('ai.pulse_tosign_title', { defaultValue: 'Partes por firmar' })}>
              <PenLine className="h-3 w-3 text-warning" />
              <span className="font-mono font-semibold tabular-nums text-warning">{toSign}</span>
            </span>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 pt-3 pb-2">
          <PulseHeader attention={attention} />
        </div>
        <div className="border-t border-border">
          <PulseBody pulse={pulse} total={total} onNavigate={onNavigate} dense />
        </div>
        {toSign > 0 && (
          <Link
            to={DAILY_REPORTS}
            className="flex items-center justify-center gap-1.5 border-t border-border px-3 py-2.5 text-xs font-semibold text-warning transition-colors hover:bg-warning/5"
          >
            <PenLine className="h-3.5 w-3.5" />
            {toSign === 1
              ? t('ai.pulse_sign_singular', { defaultValue: 'Firma tu parte diario' })
              : t('ai.pulse_sign_plural', { defaultValue: 'Firma {{n}} partes diarios', n: toSign })}
          </Link>
        )}
        {(pulse.done || 0) > 0 && (
          <Link
            to={ACTIVIDAD}
            className="flex items-center justify-center gap-1.5 border-t border-border px-3 py-2.5 text-xs font-semibold text-success transition-colors hover:bg-success/5"
          >
            {t('ai.pulse_view_done', { defaultValue: 'Ver lo que ARIA ha hecho' })}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
        <Link
          to={BANDEJA}
          className="flex items-center justify-center gap-1.5 border-t border-border px-3 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/5"
        >
          {t('ai.pulse_view_inbox', { defaultValue: 'Ver bandeja de ARIA' })}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </PopoverContent>
    </Popover>
  );
}

export default function AriaPulse({ companyId, variant = 'chip' }) {
  const navigate = useNavigate();
  const { pulse, total, isError } = useAriaPulse(companyId);

  // Sin empresa activa o ante error del agregador, no romper la superficie
  // anfitriona (TopBar / Home): simplemente no se pinta.
  if (!companyId || isError) return null;

  const onNavigate = (to) => navigate(to);

  return variant === 'full'
    ? <FullVariant pulse={pulse} total={total} onNavigate={onNavigate} />
    : <ChipVariant pulse={pulse} total={total} onNavigate={onNavigate} />;
}
